import React, { useState } from 'react';
import { css } from '@emotion/css';
import { FormikProps, withFormik } from 'formik';
import * as yup from 'yup';
import * as voicifyApi from '../../../api';
import TextField from '../../../components/forms/TextField';
import Button from '../../../components/general/Button';
import FormFieldsContainer from '../../../components/structure/FormFieldsContainer';
import { TAG_FEATURE_FLAG_OPTIONS } from '../../../constants/featureFlagIds';
import { SENTIMENT_OPTIONS } from '../../../constants/sentimentOptions';
import { color_shades_dark, color_shades_darker, color_colors_growth } from '../../../constants/colors';
import IconTooltip from '../../../components/general/IconTooltip';
import CreateTagRequest from '../../../models/configuration/CreateTagRequest';

interface CreateTagFormProps {
    onSuccess?: () => void
}

interface CreateTagFormData {
    tagName: string
    featureFlagId: string
    tagDisplayName: string
    sentiment: string
    detectionPrompt: string
    summarizationPromptAddition: string
    autoTagFlags: string
    detectionTolerance: string
}

interface CreateTagFormState {
    submitResult: 'success' | 'error' | null
    submitMessage: string
}

const InnerForm: React.FC<FormikProps<CreateTagFormData> & CreateTagFormProps & CreateTagFormState & { setSubmitState: (state: CreateTagFormState) => void }> = (props) => {
    return (
        <form onSubmit={props.handleSubmit} className={formStyle}>
            <FormFieldsContainer>
                <div className={fieldGroupStyle}>
                    <div className={labelWithTooltipStyle}>
                        <label className={labelStyle}>Feature Flag *</label>
                        <IconTooltip text="The vertical this tag belongs to (e.g. Food Ordering or Healthcare)." place="right" />
                    </div>
                    <select
                        name="featureFlagId"
                        value={props.values.featureFlagId}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        className={selectStyle}
                    >
                        <option value="">Select a feature flag...</option>
                        {TAG_FEATURE_FLAG_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {props.touched.featureFlagId && props.errors.featureFlagId && (
                        <div className={errorStyle}>{props.errors.featureFlagId}</div>
                    )}
                </div>

                <TextField
                    name="tagName"
                    value={props.values.tagName}
                    label="Tag Name *"
                    tooltip="The unique identifier for this tag (e.g. UPSELL_SUCCESSFUL). Cannot be changed after creation."
                    placeholder="e.g. UPSELL_SUCCESSFUL"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                />

                <TextField
                    name="tagDisplayName"
                    value={props.values.tagDisplayName}
                    label="Tag Display Name *"
                    tooltip="The human-readable name shown in the analytics UI (e.g. Upsell Successful)."
                    placeholder="e.g. Upsell Successful"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                />

                <div className={fieldGroupStyle}>
                    <div className={labelWithTooltipStyle}>
                        <label className={labelStyle}>Sentiment *</label>
                        <IconTooltip text="This defines how we color code the tag when displaying (positive = green, negative = red, caution = yellow, neutral = blue). Ex: ACTION_COMPLETED is a positive and thus a green tag" place="right" />
                    </div>
                    <select
                        name="sentiment"
                        value={props.values.sentiment}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        className={selectStyle}
                    >
                        {SENTIMENT_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                    {props.touched.sentiment && props.errors.sentiment && (
                        <div className={errorStyle}>{props.errors.sentiment}</div>
                    )}
                </div>

                <div className={fieldGroupStyle}>
                    <div className={labelWithTooltipStyle}>
                        <label className={labelStyle}>Detection Prompt</label>
                        <IconTooltip text="This is a prompt for configuring a generative tag, if you don't know what that means leave this blank. If you are using this to make a generative tag be sure the team (mainly Jack) is aware" place="right" />
                    </div>
                    <textarea
                        name="detectionPrompt"
                        value={props.values.detectionPrompt}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        className={textareaStyle}
                        placeholder="The AI prompt used to detect this tag in conversations"
                        rows={4}
                    />
                </div>

                <div className={fieldGroupStyle}>
                    <div className={labelWithTooltipStyle}>
                        <label className={labelStyle}>Summarization Prompt Addition</label>
                        <IconTooltip text="This is a prompt modification to the summary assuming this tag was detected. These get appeneded to 'Your summary should...'. Ex: If the tag was UNSUPPORTED_APPOINTMENT_TYPE you may want to put here 'Include the unsupported appointment type the customer requested'" place="right" />
                    </div>
                    <textarea
                        name="summarizationPromptAddition"
                        value={props.values.summarizationPromptAddition}
                        onChange={props.handleChange}
                        onBlur={props.handleBlur}
                        className={textareaStyle}
                        placeholder="Additional prompt text appended during summarization"
                        rows={3}
                    />
                </div>

                <TextField
                    name="autoTagFlags"
                    value={props.values.autoTagFlags}
                    label="Auto Tag Flags"
                    tooltip="Comma-separated flags that trigger automatic tagging (e.g. upsell-offered, pickup, etc)."
                    placeholder="Comma-separated flags, e.g. UPSOLD, upsell-offered"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                />

                <TextField
                    name="detectionTolerance"
                    value={props.values.detectionTolerance}
                    label="Detection Tolerance"
                    tooltip="A value between 0.0 and 1.0 that controls how strictly the tag is detected. ONLY SHOULD BE USING IF YOU ARE MAKING A GENERATIVE TAG"
                    placeholder="0.0 - 1.0"
                    type="number"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                />
            </FormFieldsContainer>

            {props.submitResult === 'success' && (
                <div className={successMessageStyle}>{props.submitMessage}</div>
            )}
            {props.submitResult === 'error' && (
                <div className={errorMessageStyle}>{props.submitMessage}</div>
            )}

            <div className={buttonContainerStyle}>
                <Button
                    themes={['primary']}
                    text="Create Tag"
                    type="submit"
                    loading={props.isSubmitting}
                    disabled={props.isSubmitting}
                />
            </div>
        </form>
    );
};

const FormWithFormik = withFormik<CreateTagFormProps & CreateTagFormState & { setSubmitState: (state: CreateTagFormState) => void }, CreateTagFormData>({
    mapPropsToValues: () => ({
        tagName: '',
        featureFlagId: '',
        tagDisplayName: '',
        sentiment: 'neutral',
        detectionPrompt: '',
        summarizationPromptAddition: '',
        autoTagFlags: '',
        detectionTolerance: ''
    }),
    validationSchema: yup.object().shape({
        tagName: yup.string().required('Tag name is required'),
        featureFlagId: yup.string().required('Feature flag is required'),
        tagDisplayName: yup.string().required('Display name is required'),
        sentiment: yup.string().oneOf(['positive', 'negative', 'caution', 'neutral']).required('Sentiment is required'),
        detectionPrompt: yup.string(),
        summarizationPromptAddition: yup.string(),
        autoTagFlags: yup.string(),
        detectionTolerance: yup.number().nullable().min(0, 'Must be at least 0').max(1, 'Must be at most 1')
    }),
    handleSubmit: async (values, { props, setSubmitting, resetForm }) => {
        props.setSubmitState({ submitResult: null, submitMessage: '' });

        const autoTagFlagsArray = values.autoTagFlags
            ? values.autoTagFlags.split(',').map(f => f.trim()).filter(f => f.length > 0)
            : [];

        const request: CreateTagRequest = {
            tagName: values.tagName,
            featureFlagId: values.featureFlagId,
            tagDisplayName: values.tagDisplayName,
            sentiment: values.sentiment,
            detectionPrompt: values.detectionPrompt || '',
            summarizationPromptAddition: values.summarizationPromptAddition || '',
            autoTagFlags: autoTagFlagsArray,
            detectionTolerance: values.detectionTolerance ? parseFloat(values.detectionTolerance) : null
        };

        try {
            const result = await voicifyApi.createTag(request);

            if (result.resultType === 'Ok') {
                props.setSubmitState({
                    submitResult: 'success',
                    submitMessage: `Tag "${values.tagDisplayName}" created successfully.`
                });
                resetForm();
                if (props.onSuccess) {
                    props.onSuccess();
                }
            } else {
                const errorMsg = result.errors?.length > 0
                    ? result.errors.join(', ')
                    : 'Failed to create tag. Please check your inputs and try again.';
                props.setSubmitState({
                    submitResult: 'error',
                    submitMessage: errorMsg
                });
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : 'An unexpected error occurred while creating the tag.';
            props.setSubmitState({
                submitResult: 'error',
                submitMessage: message
            });
        } finally {
            setSubmitting(false);
        }
    }
})(InnerForm);

const CreateTagFormWrapper: React.FC<CreateTagFormProps> = (props) => {
    const [submitState, setSubmitState] = useState<CreateTagFormState>({
        submitResult: null,
        submitMessage: ''
    });

    return (
        <FormWithFormik
            onSuccess={props.onSuccess}
            submitResult={submitState.submitResult}
            submitMessage={submitState.submitMessage}
            setSubmitState={setSubmitState}
        />
    );
};

const formStyle = css`
    margin-top: 8px;
`;

const fieldGroupStyle = css`
    margin-bottom: 16px;
`;

const labelStyle = css`
    display: block;
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 0;
    color: ${color_shades_darker};
`;

const labelWithTooltipStyle = css`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`;

const selectStyle = css`
    width: 100%;
    height: 48px;
    padding: 0 16px;
    border: 1px solid ${color_shades_dark};
    border-radius: 5px;
    font-family: Muli;
    font-size: 14px;
    background-color: white;
    appearance: auto;
    cursor: pointer;
    &:focus {
        outline: none;
        border-color: ${color_colors_growth};
    }
`;

const textareaStyle = css`
    width: 100%;
    padding: 12px 16px;
    border: 1px solid ${color_shades_dark};
    border-radius: 5px;
    font-family: Muli;
    font-size: 14px;
    resize: vertical;
    box-sizing: border-box;
    &:focus {
        outline: none;
        border-color: ${color_colors_growth};
    }
`;

const errorStyle = css`
    color: #D8414D;
    font-size: 12px;
    margin-top: 4px;
    font-family: Muli;
`;

const buttonContainerStyle = css`
    margin-top: 24px;
    text-align: center;
`;

const successMessageStyle = css`
    margin-top: 16px;
    padding: 12px 16px;
    background-color: rgba(88, 189, 153, 0.1);
    border: 1px solid ${color_colors_growth};
    border-radius: 5px;
    color: ${color_colors_growth};
    font-family: Muli;
    font-size: 14px;
`;

const errorMessageStyle = css`
    margin-top: 16px;
    padding: 12px 16px;
    background-color: rgba(216, 65, 77, 0.1);
    border: 1px solid #D8414D;
    border-radius: 5px;
    color: #D8414D;
    font-family: Muli;
    font-size: 14px;
`;

export default CreateTagFormWrapper;
